import { ReactElement, useCallback, useMemo } from 'react';
import { useSnackbar } from 'notistack';

import { Nullable } from '~globals/types/commons';
import { useLazyRequest } from '~hooks/useLazyRequest';
import { useIntegrationAdminDelete } from '~hooks/useIntegrationAdminDelete';
import { assignBillingListToCompanyIntegration } from '~services/admin/billingList';
import { getIntegrationProviderData } from '~utils/integrations';

import {
  DialogAddIntegration,
  DialogRenovateIntegration,
  DialogIntegrationAssignWarehouses,
  DialogAssingBillingList,
  DialogConfimation,
} from '~components/index';

import { useIntegrationsSectionContext } from '../Context';

const WARNING_TEXT_DELETE = `Recuerde que al eliminar la integración,
no podrá importar más pedidos de la misma`;

const IntegrationsSectionDialogs = (): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();

  const {
    data: { reload: reloadData },
    currentNewTab: { setValue: setCurrentNewTab },
    dialogs: {
      add: {
        open: openDialogAddIntegration,
        data: selectedAddIntegrationType,
        handleClose: handleCloseDialogAddIntegration,
      },
      renovate: {
        open: openDialogRenovateIntegration,
        data: selectedRenovateIntegrationData,
        handleClose: handleCloseDialogRenovateIntegration,
      },
      assignWarehouses: {
        open: openDialogIntegrationAssignWarehouses,
        data: selectedCompanyIntegrationIdToAssignWarehouses,
        handleClose: handleCloseDialogIntegrationAssignWarehouses,
      },
      assignBillingList: {
        open: openDialogAssignBillingList,
        data: selectedCompanyIntegrationToAssignBillingList,
        handleClose: handleCloseDialogAssignBillingList,
      },
      delete: {
        open: openDialogConfirmDeleteIntegration,
        data: selectedIntegrationDelete,
        handleClose: handleCloseDialogConfirmDeleteIntegration,
      },
    },
  } = useIntegrationsSectionContext();

  const [
    ,
    loadingAssignBillingListToCompanyIntegration,
    ,
    executeAssignBillingListToCompanyIntegration,
  ] = useLazyRequest({
    request: assignBillingListToCompanyIntegration,
    withPostSuccess: () => {
      const message = `La asignación de la lista de precios fue realizada correctamente`;

      enqueueSnackbar(message, { variant: 'success' });

      handleCloseDialogAssignBillingList();

      reloadData();
    },
    withPostFailure: () => {
      enqueueSnackbar(
        'No se pudo realizar la asignación de la lista de precios, intenté nuevamente',
        { variant: 'error' },
      );
    },
  });

  const handleAssignBillingListToCompanyIntegration = useCallback(
    async (billingListId: Nullable<string>) => {
      if (selectedCompanyIntegrationToAssignBillingList?.companyIntegrationId) {
        await executeAssignBillingListToCompanyIntegration({
          billingListId,
          companyIntegrationId:
            selectedCompanyIntegrationToAssignBillingList.companyIntegrationId,
        });
      }
    },
    [
      selectedCompanyIntegrationToAssignBillingList,
      executeAssignBillingListToCompanyIntegration,
    ],
  );

  const messageDialogConfimationDeleteIntegration = useMemo(() => {
    if (!selectedIntegrationDelete) return '';

    const providerInfo = getIntegrationProviderData(
      selectedIntegrationDelete?.integrationTypeId,
    );

    // eslint-disable-next-line max-len
    return `La integración ${providerInfo?.name} será eliminada`;
  }, [selectedIntegrationDelete]);

  const handleSuccessDeleteIntegration = useCallback(() => {
    handleCloseDialogConfirmDeleteIntegration();
    reloadData();
  }, [handleCloseDialogConfirmDeleteIntegration, reloadData]);

  const {
    loading: loadingDeleteIntegration,
    handleDeleteIntegration: executeDeleteIntegration,
  } = useIntegrationAdminDelete(handleSuccessDeleteIntegration);

  const handleDeleteIntegration = useCallback(() => {
    if (selectedIntegrationDelete)
      executeDeleteIntegration(selectedIntegrationDelete.companyIntegrationId);
  }, [selectedIntegrationDelete, executeDeleteIntegration]);

  return (
    <>
      {openDialogAddIntegration && selectedAddIntegrationType && (
        <DialogAddIntegration
          open={openDialogAddIntegration}
          onClose={() => handleCloseDialogAddIntegration()}
          integrationType={selectedAddIntegrationType}
          onSuccesSubmit={setCurrentNewTab}
        />
      )}

      {openDialogRenovateIntegration && selectedRenovateIntegrationData && (
        <DialogRenovateIntegration
          open={openDialogRenovateIntegration}
          onClose={() => handleCloseDialogRenovateIntegration()}
          data={selectedRenovateIntegrationData}
          onSuccesSubmit={setCurrentNewTab}
        />
      )}

      {openDialogIntegrationAssignWarehouses &&
        selectedCompanyIntegrationIdToAssignWarehouses && (
          <DialogIntegrationAssignWarehouses
            open={openDialogIntegrationAssignWarehouses}
            onClose={() => handleCloseDialogIntegrationAssignWarehouses()}
            companyIntegrationId={
              selectedCompanyIntegrationIdToAssignWarehouses
            }
          />
        )}

      {openDialogAssignBillingList && (
        <DialogAssingBillingList
          open={openDialogAssignBillingList}
          onClose={() => handleCloseDialogAssignBillingList()}
          onConfirm={handleAssignBillingListToCompanyIntegration}
          loadinConfirm={loadingAssignBillingListToCompanyIntegration}
        />
      )}

      <DialogConfimation
        open={openDialogConfirmDeleteIntegration}
        description={messageDialogConfimationDeleteIntegration}
        warningText={WARNING_TEXT_DELETE}
        onConfirm={handleDeleteIntegration}
        onCancel={() => handleCloseDialogConfirmDeleteIntegration()}
        loadingConfirm={loadingDeleteIntegration}
      />
    </>
  );
};

export default IntegrationsSectionDialogs;
